import type { ApiClient } from '@classgo/app-kit';
import type {
  CaptureConsentInput,
  ChangeStudentStatusInput,
  ConsentPurpose,
  ConsentTextView,
  ConsentView,
  CountryPackView,
  CreateGuardianInput,
  CreateStudentInput,
  CreateTeacherInput,
  EnrollStudentInput,
  GuardianListItem,
  InvitationView,
  LinkGuardianInput,
  PageOf,
  PersonRef,
  PublishConsentTextInput,
  PutTeacherAssignmentsInput,
  StudentHistory,
  StudentSensitiveData,
  StudentSensitiveDataInput,
  UpdateEnrollmentInput,
  UpdateGuardianInput,
  UpdateGuardianLinkInput,
  UpdateStudentInput,
  UpdateTeacherInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * The people endpoints (tickets #55, #56, #63): students, guardians and links, teachers and
 * assignments, sensitive data behind consent, invitations and accounts, photos.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const peopleApi = (api: ApiClient) => ({
  createStudent: (body: CreateStudentInput) =>
    api.request<PersonRef>('/students', { method: 'POST', body }),
  updateStudent: (id: string, body: UpdateStudentInput) =>
    api.request<PersonRef>(`/students/${id}`, { method: 'PATCH', body }),
  deleteStudent: (id: string) => api.request<void>(`/students/${id}`, { method: 'DELETE' }),
  enroll: (id: string, body: EnrollStudentInput) =>
    api.request<{ id: string }>(`/students/${id}/enrollments`, { method: 'POST', body }),
  updateEnrollment: (id: string, enrollmentId: string, body: UpdateEnrollmentInput) =>
    api.request<{ id: string }>(`/students/${id}/enrollments/${enrollmentId}`, {
      method: 'PATCH',
      body,
    }),
  changeStatus: (id: string, body: ChangeStudentStatusInput) =>
    api.request<PersonRef>(`/students/${id}/status`, { method: 'POST', body }),
  sensitive: (id: string) => api.request<StudentSensitiveData>(`/students/${id}/sensitive`),
  putSensitive: (id: string, body: StudentSensitiveDataInput) =>
    api.request<StudentSensitiveData>(`/students/${id}/sensitive`, { method: 'PUT', body }),
  createGuardian: (body: CreateGuardianInput) =>
    api.request<PersonRef>('/guardians', { method: 'POST', body }),
  updateGuardian: (id: string, body: UpdateGuardianInput) =>
    api.request<PersonRef>(`/guardians/${id}`, { method: 'PATCH', body }),
  linkGuardian: (studentId: string, body: LinkGuardianInput) =>
    api.request<void>(`/students/${studentId}/guardians`, { method: 'POST', body }),
  updateLink: (studentId: string, guardianId: string, body: UpdateGuardianLinkInput) =>
    api.request<void>(`/students/${studentId}/guardians/${guardianId}`, { method: 'PATCH', body }),
  unlink: (studentId: string, guardianId: string) =>
    api.request<void>(`/students/${studentId}/guardians/${guardianId}`, { method: 'DELETE' }),
  createTeacher: (body: CreateTeacherInput) =>
    api.request<PersonRef>('/teachers', { method: 'POST', body }),
  updateTeacher: (id: string, body: UpdateTeacherInput) =>
    api.request<PersonRef>(`/teachers/${id}`, { method: 'PATCH', body }),
  putAssignments: (id: string, body: PutTeacherAssignmentsInput) =>
    api.request<{ count: number }>(`/teachers/${id}/assignments`, { method: 'PUT', body }),
  captureConsent: (studentId: string, body: CaptureConsentInput) =>
    api.request<ConsentView[]>(`/students/${studentId}/consents`, { method: 'POST', body }),
  publishConsentText: (purpose: ConsentPurpose, body: PublishConsentTextInput) =>
    api.request<ConsentTextView>(`/consents/texts/${purpose}`, { method: 'PUT', body }),
  invite: (userId: string) =>
    api.request<InvitationView>(`/users/${userId}/invitations`, { method: 'POST' }),
  resendInvitation: (id: string) =>
    api.request<InvitationView>(`/invitations/${id}/resend`, { method: 'POST' }),
  createAccount: (studentId: string) =>
    api.request<{ id: string; email: string; status: string }>(`/students/${studentId}/account`, {
      method: 'POST',
    }),
  presignPhoto: (filename: string, contentType: string, sizeBytes: number) =>
    api.request<{ key: string; url: string; expiresAt: string }>('/uploads/presign', {
      method: 'POST',
      body: { purpose: 'photo', filename, contentType, sizeBytes },
    }),
  presignAttachment: (filename: string, contentType: string, sizeBytes: number) =>
    api.request<{ key: string; url: string; expiresAt: string }>('/uploads/presign', {
      method: 'POST',
      body: { purpose: 'attachment', filename, contentType, sizeBytes },
    }),
  downloadUrl: (key: string) =>
    api.request<{ url: string; expiresAt: string }>(
      `/uploads/download?key=${encodeURIComponent(key)}`,
    ),
});

const qs = (query: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * Query options of the people screens.
 */
export const peopleQueries = {
  countryPack: (api: ApiClient) =>
    queryOptions({
      queryKey: ['school', 'country-pack'],
      queryFn: () => api.request<CountryPackView>('/school/country-pack'),
      staleTime: 3_600_000,
    }),
  history: (api: ApiClient, studentId: string) =>
    queryOptions({
      queryKey: ['people', 'students', studentId, 'history'],
      queryFn: () => api.request<StudentHistory>(`/students/${studentId}/history`),
    }),
  sensitive: (api: ApiClient, studentId: string, enabled: boolean) =>
    queryOptions({
      queryKey: ['people', 'students', studentId, 'sensitive'],
      queryFn: () => api.request<StudentSensitiveData>(`/students/${studentId}/sensitive`),
      enabled,
      retry: false,
    }),
  consents: (api: ApiClient, studentId: string) =>
    queryOptions({
      queryKey: ['people', 'students', studentId, 'consents'],
      queryFn: () => api.request<ConsentView[]>(`/students/${studentId}/consents`),
    }),
  consentTexts: (api: ApiClient) =>
    queryOptions({
      queryKey: ['consents', 'texts'],
      queryFn: () => api.request<ConsentTextView[]>('/consents/texts'),
      staleTime: 600_000,
    }),
  guardians: (api: ApiClient, query: { search?: string; page?: number }) =>
    queryOptions({
      queryKey: ['people', 'guardians', query],
      queryFn: () =>
        api.request<PageOf<GuardianListItem>>(`/guardians${qs({ pageSize: 50, ...query })}`),
    }),
  invitations: (api: ApiClient, userId: string) =>
    queryOptions({
      queryKey: ['people', 'invitations', userId],
      queryFn: () => api.request<{ items: InvitationView[] }>(`/invitations?userId=${userId}`),
      enabled: userId !== '',
    }),
  photoUrl: (api: ApiClient, key: string | null) =>
    queryOptions({
      queryKey: ['uploads', 'download', key],
      queryFn: () =>
        api.request<{ url: string; expiresAt: string }>(
          `/uploads/download?key=${encodeURIComponent(key ?? '')}`,
        ),
      enabled: Boolean(key),
      staleTime: 240_000,
    }),
};
